import { create } from 'zustand';
import apiClient from '../lib/api';

export interface EmployeeDocument {
    id: string;
    tenantId: string;
    employeeId: string;
    employeeFirstName?: string;
    employeeLastName?: string;
    title: string;
    type: string;
    fileUrl: string;
    isVerified: boolean;
    uploadedAt: string;
    createdAt: string;
    updatedAt: string;
}

interface DocumentState {
    documents: EmployeeDocument[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchDocuments: (filters?: any) => Promise<void>;
    createDocument: (data: Partial<EmployeeDocument>) => Promise<void>;
    updateDocument: (id: string, data: Partial<EmployeeDocument>) => Promise<void>;
    deleteDocument: (id: string) => Promise<void>;
}

export const useDocumentStore = create<DocumentState>((set, get) => ({
    documents: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchDocuments: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/documents', { params: filters });
            const { data, pagination } = response.data;
            set({ documents: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch documents', isLoading: false });
        }
    },
    createDocument: async (data: Partial<EmployeeDocument>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/documents', data);
            const newDocument = response.data;
            set((state) => ({
                documents: [newDocument, ...state.documents],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create document', isLoading: false });
            throw error;
        }
    },
    updateDocument: async (id: string, data: Partial<EmployeeDocument>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/documents/${id}`, data);
            const updatedDocument = response.data;
            set((state) => ({
                documents: state.documents.map((o) => (o.id === id ? updatedDocument : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update document', isLoading: false });
            throw error;
        }
    },
    deleteDocument: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/people/documents/${id}`);
            set((state) => ({
                documents: state.documents.filter((d) => d.id !== id),
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete document' });
            throw error;
        }
    },
}));
